import { Platform } from 'react-native';
import Constants from 'expo-constants';
import { CryptoDigestAlgorithm, digest as nativeDigest } from 'expo-crypto';
import { v4 as uuidv4 } from 'uuid';
import { databaseService } from '@/services/databaseService';
import { globalState } from '@/state/globalState';
import { supabase } from '@/services/supabase';
import { getRuntimeClientBuild } from '@/services/runtime/clientBuild';
import { getStableRecoveryDeviceId } from '@/services/recovery/RecoveryRuntimeGate';
import { getLastSyncTimestamp, registerSupportDiagnosticSyncHook } from '@/services/syncService';
import { hasSyncExecution } from '@/services/sync/SyncExecutionLease';
import { enqueueLegacySupportCheck } from '@/services/runtime/supportCollectionQueue';
import { SettingsSupportLogger } from '@/utils/logger';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import {
  isSupportMediaLocalFailure,
  isSupportMediaSnapshotUri,
  prepareNativeSupportMediaSnapshot,
  supportMediaLocalFailure,
} from '@/services/diagnostics/NativeSupportMediaSnapshot';
import {
  SUPPORT_FULL_STATE_MAX_PART_BYTES,
  SUPPORT_FULL_STATE_PLAN_BATCH_SIZE,
  SUPPORT_FULL_STATE_SCHEMA,
  type SupportFullStateManifest,
  type SupportFullStatePart,
  type SupportFullStateSnapshot,
} from '@/types/supportFullState';

export const SUPPORT_FULL_STATE_BUCKET = 'app_guincho_support_full_state';
const PROTOCOL = 1;
const PART_UPLOAD_CONCURRENCY = 4;
const SMALL_ROW_PART_THRESHOLD = 256 * 1024;
type Scope = { companyId: string; userId: string; deviceId: string; clientBuild: number };
type Plan = { sha512_hex: string; availability: 'available' | 'upload_required' | 'verification_required' | 'retry_later' };
const FULL_STATE_STAGES = ['scope', 'prepare_request', 'claim', 'capture', 'prepare_media', 'partition', 'plan', 'read_part', 'upload_part',
  'verify_part', 'register_part', 'upload_manifest', 'verify_manifest', 'finalize', 'cleanup_media'] as const;
type FullStateStage = typeof FULL_STATE_STAGES[number];
type FullStateTrace = { stage: FullStateStage; requestId: string | null; partOrdinal: number | null };
const REMOTE_FAILURE_REASONS = ['timeout', 'unavailable', 'identity_incomplete', 'session_unavailable',
  'session_divergent', 'identity_changed', 'part_invalid', 'media_unavailable', 'media_empty', 'media_changed',
  'verification_rejected', 'verification_failed', 'too_many_parts', 'plan_incomplete', 'retry_later',
  'plan_mismatch', 'part_changed', 'manifest_too_large', 'finalize_failed', 'ticket_failed', 'request_not_claimed',
  'local_media_inspect_failed', 'local_media_copy_failed', 'local_media_read_failed', 'local_media_cleanup_failed'] as const;

export const normalizeSupportFullStateFailureCode = (stage: unknown, error: unknown): string => {
  const knownStage = FULL_STATE_STAGES.find(value => value === stage) ?? 'unknown';
  try {
    if (error instanceof TypeError) return `fs_${knownStage}_type_error`;
    const candidates = typeof error === 'object' && error !== null
      ? [(error as { message?: unknown }).message, (error as { code?: unknown }).code]
      : [error];
    const reason = REMOTE_FAILURE_REASONS.find(value => candidates.some(candidate =>
      candidate === `support_full_state_${value}`)) ?? 'unknown';
    return `fs_${knownStage}_${reason}`;
  } catch {
    return `fs_${knownStage}_unknown`;
  }
};
const encoder = new TextEncoder();
const logger = SettingsSupportLogger.Main;
const hex = (bytes: Uint8Array): string => Array.from(bytes).map(value => value.toString(16).padStart(2, '0')).join('');
const digest = async (bytes: Uint8Array): Promise<string> =>
  hex(new Uint8Array(await nativeDigest(CryptoDigestAlgorithm.SHA512, bytes as unknown as BufferSource)));
const first = <T>(data: T | T[] | null): T | null => Array.isArray(data) ? data[0] ?? null : data;
const timeout = <T>(promise: PromiseLike<T>, ms = 30_000): Promise<T> => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('support_full_state_timeout')), ms);
  Promise.resolve(promise).then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
});

const yieldToNormalSync = async (requestId: string): Promise<void> => {
  if (!hasSyncExecution()) return;
  const startedAt = Date.now();
  while (hasSyncExecution() && Date.now() - startedAt < 5_000) {
    await new Promise<void>(resolve => setTimeout(resolve, 50));
  }
  if (hasSyncExecution()) {
    // O coordenador registra a falha de forma retomável; a exportação nunca
    // mantém a lease de sync ocupada enquanto aguarda outra execução.
    throw new Error('support_full_state_sync_busy');
  }
  logger.debug?.(`[EXPORT] sync_yield_complete request=${requestId}`);
};

const safeFailureCode = (error: unknown): string => {
  const message = error instanceof Error ? error.message : '';
  const knownMessage = message.match(/(?:support_full_state|full_state)_[a-z0-9_]+/i)?.[0];
  if (knownMessage) return knownMessage.toLowerCase();
  const backendCode = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code ?? '')
    : '';
  if (/^[a-z0-9_-]{1,40}$/i.test(backendCode)) return `backend_${backendCode.toLowerCase()}`;
  if (error instanceof TypeError) return 'network_or_runtime_type_error';
  return 'unknown';
};

const isExpectedFullStateAvailabilityFailure = (error: unknown): boolean => {
  const code = safeFailureCode(error);
  return code === 'support_full_state_unavailable' ||
    code === 'support_full_state_identity_incomplete' ||
    code === 'support_full_state_session_unavailable' ||
    code === 'support_full_state_session_divergent' ||
    code === 'support_full_state_retry_later';
};

const reportFullStateFailure = (error: unknown, trace: FullStateTrace): void => {
  const code = safeFailureCode(error);
  const sanitizedError = new Error(`support_full_state_failed:${trace.stage}:${code}`);
  sanitizedError.name = 'SupportFullStateError';
  const message = `[EXPORT] Falha stage=${trace.stage} code=${code} request=${trace.requestId ?? 'none'} part=${trace.partOrdinal ?? 'none'}`;
  if (isExpectedFullStateAvailabilityFailure(error)) {
    return;
  }
  // A captura explícita abaixo já cria o único evento Sentry desta falha;
  // warning mantém a ocorrência no buffer/breadcrumb sem duplicar evento.
  logger.warn(message);
  captureSentryExceptionWithScope(sanitizedError, {
    tags: { flow: 'support_full_state', stage: trace.stage, error_code: code },
    context: { requestId: trace.requestId, partOrdinal: trace.partOrdinal },
    fingerprint: ['support_full_state', trace.stage, code],
    dedupeKey: `support_full_state:${trace.stage}:${code}`,
    dedupeWindowMs: 30_000,
  });
};

const canonicalize = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, canonicalize(item)]));
  return value;
};
const canonical = (value: unknown): Uint8Array => encoder.encode(JSON.stringify(canonicalize(value)));
const mimeFor = (value: string): SupportFullStatePart['mime'] => {
  const path = value.toLowerCase();
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.pdf')) return 'application/pdf';
  return 'image/jpeg';
};

const getLocalScope = (): Scope => {
  if (!globalState.network.isConnected.get() || !globalState.database.isInitialized.get()) throw new Error('support_full_state_unavailable');
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const clientBuild = getRuntimeClientBuild();
  if (!user?.id || profile?.id !== user.id || !profile.empresa_id || company?.id !== profile.empresa_id || clientBuild <= 0) throw new Error('support_full_state_identity_incomplete');
  return { companyId: profile.empresa_id, userId: user.id, deviceId: getStableRecoveryDeviceId(), clientBuild };
};

const requireScope = async (): Promise<Scope> => {
  const scope = getLocalScope();
  const { data, error } = await timeout(supabase.auth.getUser());
  if (error || data.user?.id !== scope.userId) throw new Error('support_full_state_session_divergent');
  return scope;
};

const createPart = async (input: Omit<SupportFullStatePart, 'ordinal' | 'sha512' | 'byteLength'>): Promise<SupportFullStatePart> => {
  const bytes = await input.read();
  if (!bytes.length || bytes.length > SUPPORT_FULL_STATE_MAX_PART_BYTES) throw new Error('support_full_state_part_invalid');
  return { ...input, ordinal: -1, sha512: await digest(bytes), byteLength: bytes.length };
};

const rowIdentity = (row: Record<string, unknown>, serialized: string): string => String(row.id ?? serialized);

/**
 * Tabelas grandes ocupam o menor número possível de partes de até 4 MiB e
 * segmentos pequenos são agrupados em NDJSON. A ordem por identidade mantém
 * o conteúdo determinístico sem calcular SHA-512 para cada linha.
 */
export const buildSupportFullStateParts = async (
  snapshot: SupportFullStateSnapshot,
  readMediaUri: (sourceUri: string) => string | null = uri => uri,
): Promise<SupportFullStatePart[]> => {
  const parts: SupportFullStatePart[] = [];
  const append = async (input: Omit<SupportFullStatePart, 'ordinal' | 'sha512' | 'byteLength'>) => {
    const part = await createPart(input); part.ordinal = parts.length; parts.push(part);
  };
  let smallRows: Array<{ serialized: string; logical: Record<string, unknown>; byteLength: number }> = [];
  let smallRowsBytes = 0;
  const flushSmallRows = async () => {
    if (!smallRows.length) return;
    const bundled = smallRows.length > 1;
    const payload = smallRows.map(item => item.serialized).join('\n');
    const logical = bundled
      ? { bundle: true, encoding: 'ndjson', entries: smallRows.map(item => item.logical) }
      : smallRows[0].logical;
    await append({ mime: bundled ? 'application/x-ndjson' : 'application/json', kind: 'row', logical, read: async () => encoder.encode(payload) });
    smallRows = []; smallRowsBytes = 0;
  };
  const appendRowPayload = async (serialized: string, logical: Record<string, unknown>) => {
    const byteLength = encoder.encode(serialized).length;
    if (byteLength >= SMALL_ROW_PART_THRESHOLD) {
      await flushSmallRows();
      await append({ mime: 'application/json', kind: 'row', logical, read: async () => encoder.encode(serialized) });
      return;
    }
    const separatorBytes = smallRows.length ? 1 : 0;
    if (smallRowsBytes + separatorBytes + byteLength > SUPPORT_FULL_STATE_MAX_PART_BYTES) await flushSmallRows();
    smallRows.push({ serialized, logical, byteLength });
    smallRowsBytes += (smallRows.length > 1 ? 1 : 0) + byteLength;
  };
  for (const table of Object.keys(snapshot.tables).sort()) {
    const bucket = '00';
    const tableRows = snapshot.tables[table]
      .map(row => {
        const serialized = JSON.stringify(row);
        return { identity: rowIdentity(row, serialized), serialized, byteLength: encoder.encode(serialized).length };
      })
      .sort((a, b) => a.identity < b.identity ? -1 : a.identity > b.identity ? 1 : 0);
    const prefix = `{"bucket":"${bucket}","rows":[`;
    const suffix = `],"table":${JSON.stringify(table)}}`;
    const wrapperBytes = encoder.encode(prefix + suffix).length;
    const segments: string[][] = [];
    let segment: string[] = [];
    let segmentBytes = wrapperBytes;
    for (const entry of tableRows) {
      const separatorBytes = segment.length ? 1 : 0;
      if (segmentBytes + separatorBytes + entry.byteLength <= SUPPORT_FULL_STATE_MAX_PART_BYTES) {
        segment.push(entry.serialized); segmentBytes += separatorBytes + entry.byteLength; continue;
      }
      if (segment.length) segments.push(segment);
      const raw = encoder.encode(`{"row":${entry.serialized},"table":${JSON.stringify(table)}}`);
      if (raw.length > SUPPORT_FULL_STATE_MAX_PART_BYTES) {
        await flushSmallRows();
        const hash = await digest(raw);
        const count = Math.ceil(raw.length / SUPPORT_FULL_STATE_MAX_PART_BYTES);
        for (let index = 0; index < count; index += 1) {
          const offset = index * SUPPORT_FULL_STATE_MAX_PART_BYTES;
          await append({ mime: 'application/octet-stream', kind: 'row_fragment', logical: { table, bucket, row_sha512: hash, fragment: index, fragments: count, encoding: 'utf8-json' }, read: async () => raw.slice(offset, offset + SUPPORT_FULL_STATE_MAX_PART_BYTES) });
        }
        segment = []; segmentBytes = wrapperBytes;
      } else {
        segment = [entry.serialized]; segmentBytes = wrapperBytes + entry.byteLength;
      }
    }
    if (segment.length) segments.push(segment);
    for (let index = 0; index < segments.length; index += 1) {
      const rows = segments[index];
      await appendRowPayload(prefix + rows.join(',') + suffix, { table, bucket, segment: index, segments: segments.length, encoding: 'json' });
    }
  }
  await flushSmallRows();
  let unavailableMedia: Array<{ serialized: string; logical: Record<string, unknown>; byteLength: number }> = [];
  let unavailableMediaBytes = 0;
  const flushUnavailableMedia = async () => {
    if (!unavailableMedia.length) return;
    const bundled = unavailableMedia.length > 1;
    const payload = unavailableMedia.map(item => item.serialized).join('\n');
    const logical = bundled
      ? { bundle: true, encoding: 'ndjson', entries: unavailableMedia.map(item => item.logical) }
      : unavailableMedia[0].logical;
    await append({ mime: bundled ? 'application/x-ndjson' : 'application/json', kind: 'media_unavailable', logical, read: async () => encoder.encode(payload) });
    unavailableMedia = []; unavailableMediaBytes = 0;
  };
  for (const media of snapshot.media.filter(item => !isSupportMediaSnapshotUri(item.sourceUri))) {
    try {
      const readUri = readMediaUri(media.sourceUri);
      if (readUri === null) throw new Error('support_full_state_media_unavailable');
      const mediaParts = await describeMedia(readUri, media.sourceUri);
      for (const source of mediaParts) {
        await append({ mime: source.mime, kind: source.count === 1 ? 'media' : 'media_fragment', logical: { media_id: media.mediaId, source_uri: media.sourceUri, origin: media.origin, fragment: source.index, fragments: source.count, content_type: media.contentType }, read: source.read });
      }
    } catch (error) {
      if (isSupportMediaLocalFailure(error)) throw error;
      const payload = { media_id: media.mediaId, source_uri: media.sourceUri, origin: media.origin, status: 'unavailable' };
      const serialized = JSON.stringify(canonicalize(payload));
      const byteLength = encoder.encode(serialized).length;
      const separatorBytes = unavailableMedia.length ? 1 : 0;
      if (unavailableMediaBytes + separatorBytes + byteLength > SUPPORT_FULL_STATE_MAX_PART_BYTES) await flushUnavailableMedia();
      unavailableMedia.push({ serialized, logical: payload, byteLength });
      unavailableMediaBytes += (unavailableMedia.length > 1 ? 1 : 0) + byteLength;
    }
  }
  await flushUnavailableMedia();
  if (!parts.length) await append({ mime: 'application/json', kind: 'row', logical: { empty: true }, read: async () => canonical({ empty: true }) });
  return parts;
};

type MediaChunk = { index: number; count: number; mime: SupportFullStatePart['mime']; read(): Promise<Uint8Array> };
const describeMedia = async (uri: string, originalUri = uri): Promise<MediaChunk[]> => {
  if (Platform.OS === 'web') {
    const local = /^checklist-(photos|signatures|marcacoes)\//.test(uri);
    let blob: Blob;
    if (local) {
      const { fetchFile } = require('@/utils/file/fileHelpers') as typeof import('@/utils/file/fileHelpers');
      blob = await fetchFile(uri) as Blob;
    } else {
      const response = await fetch(uri); if (!response.ok) throw new Error('support_full_state_media_unavailable');
      blob = await response.blob();
    }
    const head = new Uint8Array(await blob.slice(0, 64).arrayBuffer());
    const prefix = String.fromCharCode(...head).trimStart().toLowerCase();
    if (/text\/html/i.test(blob.type) || prefix.startsWith('<!doctype') || prefix.startsWith('<html')) {
      throw new Error('support_full_state_media_html');
    }
    const count = Math.ceil(blob.size / SUPPORT_FULL_STATE_MAX_PART_BYTES);
    if (!count) throw new Error('support_full_state_media_empty');
    return Array.from({ length: count }, (_, index) => ({ index, count, mime: count === 1 ? mimeFor(uri) : 'application/octet-stream' as const,
      read: async () => new Uint8Array(await blob.slice(index * SUPPORT_FULL_STATE_MAX_PART_BYTES, Math.min(blob.size, (index + 1) * SUPPORT_FULL_STATE_MAX_PART_BYTES)).arrayBuffer()) }));
  }
  try {
    const fileSystem = require('expo-file-system') as typeof import('expo-file-system');
    const file = new fileSystem.File(uri) as unknown as { open(mode: unknown): { readBytes(length: number): Uint8Array; close(): void; offset: number | null }; size?: number };
    const handle = file.open(fileSystem.FileMode.ReadOnly);
    const descriptors: MediaChunk[] = [];
    try {
      let offset = 0;
      while (true) {
        const chunk = new Uint8Array(handle.readBytes(SUPPORT_FULL_STATE_MAX_PART_BYTES));
        if (!chunk.length) break;
        const hash = await digest(chunk); const size = chunk.length; const index = descriptors.length; const chunkOffset = offset;
        descriptors.push({ index, count: -1, mime: 'application/octet-stream', read: async () => readNativeChunk(fileSystem, uri, chunkOffset, size, hash) });
        offset += size;
      }
    } finally { handle.close(); }
    if (!descriptors.length) throw new Error('support_full_state_media_empty');
    descriptors.forEach((part, index) => { part.count = descriptors.length; part.mime = descriptors.length === 1 ? mimeFor(originalUri) : 'application/octet-stream'; });
    return descriptors;
  } catch (error) {
    if (error instanceof Error && error.message === 'support_full_state_media_empty') throw error;
    if (!/^(file|content):\/\//.test(uri)) throw error;
    throw supportMediaLocalFailure('local_media_read_failed');
  }
};

const readNativeChunk = async (fileSystem: typeof import('expo-file-system'), uri: string, offset: number, size: number, expectedHash: string): Promise<Uint8Array> => {
  try {
    const file = new fileSystem.File(uri) as unknown as { open(mode: unknown): { readBytes(length: number): Uint8Array; close(): void; offset: number | null } };
    const handle = file.open(fileSystem.FileMode.ReadOnly);
    try {
      handle.offset = offset;
      const bytes = new Uint8Array(handle.readBytes(size));
      if (bytes.length !== size || await digest(bytes) !== expectedHash) throw new Error('support_full_state_media_changed');
      return bytes;
    } finally { handle.close(); }
  } catch (error) {
    if (error instanceof Error && error.message === 'support_full_state_media_changed') throw error;
    throw supportMediaLocalFailure('local_media_read_failed');
  }
};

const storage = {
  async upload(path: string, body: Uint8Array, contentType: string, metadata: Record<string, string | number>) {
    const { data, error } = await supabase.storage.from(SUPPORT_FULL_STATE_BUCKET).upload(path, body, { contentType, upsert: false, metadata });
    if (error) {
      // Uma queda entre upload e registro deixa somente o objeto. Nesta nova
      // tentativa o planner ainda pede upload; o registro posterior confere
      // hash/tamanho no Storage antes de reaproveitá-lo.
      const status = String((error as { statusCode?: string | number }).statusCode ?? '');
      if (status !== '409') throw error;
    }
    return data?.path ?? path;
  },
};

const partDescriptor = (part: SupportFullStatePart) => ({ sha512: part.sha512, bytes: part.byteLength, mime: part.mime });
const rpc = async <T>(name: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await timeout(supabase.rpc(name, args));
  if (error) throw error;
  return data as T;
};

const verifyRemoteContent = async (input: {
  scope: Scope; requestId: string; kind: 'part' | 'manifest'; sha512: string;
  bytes: number; mime: string; exportId: string | null;
}): Promise<void> => {
  const { data: sessionData, error: sessionError } = await timeout(supabase.auth.getSession());
  const token = sessionData.session?.access_token;
  if (sessionError || !token) throw new Error('support_full_state_session_unavailable');
  const body = {
    operation: 'verify_content' as const, request_id: input.requestId, empresa_id: input.scope.companyId,
    device_id: input.scope.deviceId, client_build: input.scope.clientBuild, content_kind: input.kind,
    sha512_hex: input.sha512, size_bytes: input.bytes, mime_type: input.mime,
    ...(input.exportId ? { export_id: input.exportId } : {}),
  };
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data, error } = await timeout(supabase.functions.invoke('support-full-state-retention', {
        body, headers: { Authorization: `Bearer ${token}` },
      }));
      if (!error && (data as { status?: string } | null)?.status === 'content_verified') return;
      lastError = error ?? new Error('support_full_state_verification_rejected');
    } catch (error) { lastError = error; }
  }
  throw lastError instanceof Error ? lastError : new Error('support_full_state_verification_failed');
};

const uploadExport = async (scope: Scope, requestId: string, trace: FullStateTrace): Promise<{ protocol: string }> => {
  const startedAt = Date.now();
  const assertScope = async () => {
    const current = getLocalScope();
    if (current.companyId !== scope.companyId || current.userId !== scope.userId || current.deviceId !== scope.deviceId || current.clientBuild !== scope.clientBuild) throw new Error('support_full_state_identity_changed');
  };
  await assertScope();
  trace.stage = 'capture';
  logger.info(`[EXPORT] capture_started request=${requestId}`);
  await yieldToNormalSync(requestId);
  await assertScope();
  const snapshot = await databaseService.captureFullStateSnapshot(scope.companyId, scope.userId);
  const capturedAt = Date.now();
  trace.stage = 'prepare_media';
  const mediaSnapshot = Platform.OS === 'web' ? null : await prepareNativeSupportMediaSnapshot(snapshot.media, error =>
    reportFullStateFailure(error, { stage: 'cleanup_media', requestId, partOrdinal: null }));
  try {
    if (mediaSnapshot) logger.info(`[EXPORT] media_snapshot_ready request=${requestId} files=${mediaSnapshot.fileCount} bytes=${mediaSnapshot.byteLength}`);
    return await uploadPreparedExport(scope, requestId, trace, snapshot, mediaSnapshot?.readUri, capturedAt, startedAt);
  } finally {
    try { mediaSnapshot?.dispose(); } catch (error) {
      reportFullStateFailure(error, { stage: 'cleanup_media', requestId, partOrdinal: null });
    }
  }
};

const uploadPreparedExport = async (
  scope: Scope, requestId: string, trace: FullStateTrace, snapshot: SupportFullStateSnapshot,
  readMediaUri: ((sourceUri: string) => string | null) | undefined, capturedAt: number, startedAt: number,
): Promise<{ protocol: string }> => {
  const assertScope = async () => {
    const current = getLocalScope();
    if (current.companyId !== scope.companyId || current.userId !== scope.userId || current.deviceId !== scope.deviceId || current.clientBuild !== scope.clientBuild) throw new Error('support_full_state_identity_changed');
  };
  trace.stage = 'partition';
  logger.info(`[EXPORT] partition_started request=${requestId}`);
  await yieldToNormalSync(requestId);
  await assertScope();
  const parts = await buildSupportFullStateParts(snapshot, readMediaUri);
  const preparedAt = Date.now();
  const byHash = new Map<string, SupportFullStatePart>();
  parts.forEach(part => {
    const existing = byHash.get(part.sha512);
    if (existing) part.mime = existing.mime;
    else byHash.set(part.sha512, part);
  });
  const remoteParts = [...byHash.values()];
  if (remoteParts.length > 25_000) throw new Error('support_full_state_too_many_parts');
  logger.info(`[EXPORT] Preparação local concluída rows=${Object.values(snapshot.tables).reduce((total, rows) => total + rows.length, 0)} parts=${parts.length} unique_parts=${remoteParts.length} bytes=${remoteParts.reduce((total, part) => total + part.byteLength, 0)} capture_ms=${capturedAt - startedAt} partition_ms=${preparedAt - capturedAt}`);
  for (let offset = 0; offset < remoteParts.length; offset += SUPPORT_FULL_STATE_PLAN_BATCH_SIZE) {
    trace.stage = 'plan';
    await yieldToNormalSync(requestId);
    await assertScope();
    const batch = remoteParts.slice(offset, offset + SUPPORT_FULL_STATE_PLAN_BATCH_SIZE);
    const planned = await rpc<Plan[]>('app_guincho_plan_full_state_parts_v1', {
      p_request_id: requestId, p_empresa_id: scope.companyId, p_device_id: scope.deviceId,
      p_client_build: scope.clientBuild, p_parts: batch.map(partDescriptor),
    });
    if (planned.length !== batch.length) throw new Error('support_full_state_plan_incomplete');
    let nextDecision = 0;
    let firstFailure: { error: unknown; stage: FullStateStage; ordinal: number } | null = null;
    const worker = async () => {
      while (!firstFailure && nextDecision < planned.length) {
        const decisionIndex = nextDecision; nextDecision += 1;
        const decision = planned[decisionIndex];
        const ordinal = offset + decisionIndex;
        let stage: FullStateStage = 'plan';
        try {
          await yieldToNormalSync(requestId);
          await assertScope();
          if (decision.availability === 'retry_later') throw new Error('support_full_state_retry_later');
          if (decision.availability === 'available') continue;
          const part = byHash.get(decision.sha512_hex);
          if (!part) throw new Error('support_full_state_plan_mismatch');
          await assertScope();
          if (decision.availability === 'upload_required') {
            stage = 'read_part';
            const path = `${scope.companyId}/${scope.userId}/${scope.deviceId}/${part.sha512}.part`;
            const bytes = await part.read();
            if (bytes.length !== part.byteLength || await digest(bytes) !== part.sha512) throw new Error('support_full_state_part_changed');
            stage = 'upload_part';
            await storage.upload(path, bytes, part.mime, { sha512: part.sha512, bytes: part.byteLength, mime: part.mime });
          }
          stage = 'verify_part';
          await assertScope();
          await verifyRemoteContent({ scope, requestId, kind: 'part', sha512: part.sha512, bytes: part.byteLength, mime: part.mime, exportId: null });
          stage = 'register_part';
          await assertScope();
          await rpc('app_guincho_register_full_state_part_v1', {
            p_request_id: requestId, p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild,
            p_sha512_hex: part.sha512, p_size_bytes: part.byteLength, p_mime_type: part.mime,
          });
        } catch (error) {
          firstFailure ??= { error, stage, ordinal };
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PART_UPLOAD_CONCURRENCY, planned.length) }, worker));
    if (firstFailure) {
      const failure = firstFailure as { error: unknown; stage: FullStateStage; ordinal: number };
      trace.stage = failure.stage; trace.partOrdinal = failure.ordinal;
      throw failure.error;
    }
  }
  const exportId = uuidv4();
  const mimeTypes = [...new Set(parts.map(part => part.mime))];
  const kinds = [...new Set(parts.map(part => part.kind))];
  const manifest: SupportFullStateManifest = {
    schema: SUPPORT_FULL_STATE_SCHEMA, generatedAt: new Date().toISOString(), exportId, requestId,
    tableCounts: Object.fromEntries(Object.entries(snapshot.tables).map(([name, rows]) => [name, rows.length])),
    identity: { empresaId: scope.companyId, userId: scope.userId, deviceId: scope.deviceId },
    client: { appVersion: Constants.expoConfig?.version ?? 'unknown', build: scope.clientBuild, platform: Platform.OS, syncStatus: globalState.sync.lastStatus.get(), lastSyncAt: getLastSyncTimestamp({ companyId: scope.companyId, userId: scope.userId }) || null },
    logicalPartCount: parts.length,
    mimeTypes, kinds,
    parts: parts.map(part => [part.ordinal, part.sha512, part.byteLength, mimeTypes.indexOf(part.mime), kinds.indexOf(part.kind), part.logical]),
  };
  const manifestBytes = canonical(manifest);
  if (manifestBytes.length > SUPPORT_FULL_STATE_MAX_PART_BYTES) throw new Error('support_full_state_manifest_too_large');
  const manifestHash = await digest(manifestBytes);
  const manifestPath = `${scope.companyId}/${scope.userId}/${scope.deviceId}/${requestId}/${exportId}.manifest.json`;
  trace.partOrdinal = null;
  trace.stage = 'upload_manifest';
  await yieldToNormalSync(requestId);
  await assertScope();
  await storage.upload(manifestPath, manifestBytes, 'application/vnd.app-guincho.support-full-state-manifest+json', {
    sha512: manifestHash, bytes: manifestBytes.length, export_id: exportId, request_id: requestId, part_count: remoteParts.length,
    total_bytes: remoteParts.reduce((total, part) => total + part.byteLength, 0),
  });
  trace.stage = 'verify_manifest';
  await assertScope();
  await verifyRemoteContent({ scope, requestId, kind: 'manifest', sha512: manifestHash, bytes: manifestBytes.length, mime: 'application/vnd.app-guincho.support-full-state-manifest+json', exportId });
  trace.stage = 'finalize';
  await assertScope();
  const result = first(await rpc<{ protocol: string }[]>('app_guincho_finalize_full_state_export_v1', {
    p_export_id: exportId, p_request_id: requestId, p_empresa_id: scope.companyId, p_device_id: scope.deviceId,
    p_manifest_sha512_hex: manifestHash, p_manifest_bytes: manifestBytes.length, p_client_build: scope.clientBuild,
    p_parts: remoteParts.map((part, ordinal) => ({ ...partDescriptor(part), ordinal })),
  }));
  if (!result?.protocol) throw new Error('support_full_state_finalize_failed');
  logger.info(`[EXPORT] Envio concluído parts=${remoteParts.length} bytes=${remoteParts.reduce((total, part) => total + part.byteLength, 0)} total_ms=${Date.now() - startedAt}`);
  return result;
};

export const sendSupportFullStateExplicitly = async (): Promise<{ protocol: string }> => {
  const trace: FullStateTrace = { stage: 'scope', requestId: null, partOrdinal: null };
  let scope: Scope | null = null;
  try {
    scope = await requireScope();
    trace.stage = 'prepare_request';
    const ticket = first(await rpc<{ request_id: string }[]>('app_guincho_prepare_manual_full_state_export_v1', {
      p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild, p_protocol: PROTOCOL,
    }));
    if (!ticket?.request_id) throw new Error('support_full_state_ticket_failed');
    trace.requestId = ticket.request_id;
    return await uploadExport(scope, ticket.request_id, trace);
  } catch (error) {
    const failedAt = { ...trace };
    const remoteFailureCode = normalizeSupportFullStateFailureCode(failedAt.stage, error);
    // Libera a lease manual para uma nova tentativa sem registrar payload nem
    // detalhes locais no backend.
    try {
      if (!scope || !trace.requestId) throw new Error('support_full_state_request_not_claimed');
      await rpc('app_guincho_fail_full_state_export_request_v1', {
        p_request_id: trace.requestId, p_device_id: scope.deviceId, p_error_code: remoteFailureCode,
      });
    } catch {
      // A falha original e mais útil para a UI; não expomos seu conteúdo.
    }
    reportFullStateFailure(error, failedAt);
    throw error;
  }
};

/** Reutiliza capture/partes/Storage do full-state para uma lease do lote. */
export const sendSupportFullStateForPostUpdateRequest = async (requestId: string): Promise<{ protocol: string }> => {
  const trace: FullStateTrace = { stage: 'scope', requestId, partOrdinal: null };
  const scope = await requireScope();
  try {
    return await uploadExport(scope, requestId, trace);
  } catch (error) {
    reportFullStateFailure(error, trace);
    throw error;
  }
};

let checker: Promise<void> | null = null;
let nextCheckAt = 0;
export const checkPendingSupportFullStateRequests = (_trigger: string): Promise<void> => {
  if (checker || Date.now() < nextCheckAt) return checker ?? Promise.resolve();
  checker = (async () => {
    const trace: FullStateTrace = { stage: 'scope', requestId: null, partOrdinal: null };
    let requestFailureReported = false;
    try {
      const scope = await requireScope();
      trace.stage = 'claim';
      const ticket = first(await rpc<{ request_id: string }[]>('app_guincho_claim_full_state_export_request_v1', {
        p_empresa_id: scope.companyId, p_device_id: scope.deviceId, p_client_build: scope.clientBuild, p_protocol: PROTOCOL,
      }));
      if (ticket?.request_id) {
        const uploadTrace: FullStateTrace = { stage: 'capture', requestId: ticket.request_id, partOrdinal: null };
        try { await uploadExport(scope, ticket.request_id, uploadTrace); }
        catch (error) {
          const failedAt = { ...uploadTrace };
          const remoteFailureCode = normalizeSupportFullStateFailureCode(failedAt.stage, error);
          reportFullStateFailure(error, failedAt);
          requestFailureReported = true;
          try {
            await rpc('app_guincho_fail_full_state_export_request_v1', { p_request_id: ticket.request_id, p_device_id: scope.deviceId, p_error_code: remoteFailureCode });
          } catch (releaseError) {
            reportFullStateFailure(releaseError, { stage: 'claim', requestId: ticket.request_id, partOrdinal: null });
          }
        }
      }
      nextCheckAt = Date.now() + 15_000;
    } catch (error) {
      if (!requestFailureReported) reportFullStateFailure(error, trace);
      nextCheckAt = Date.now() + 30_000;
    }
  })().finally(() => { checker = null; });
  return checker;
};

/** Permite isolar o estado temporal do verificador nos testes unitários. */
export const __resetSupportFullStateRuntimeForTests = (): void => {
  checker = null;
  nextCheckAt = 0;
};

registerSupportDiagnosticSyncHook(status => {
  void enqueueLegacySupportCheck(() => checkPendingSupportFullStateRequests(`sync_${status}`));
});
